import React, { useState, useCallback, useEffect } from "react";
import { Button, Group, Text, Stack, Box } from "@mantine/core";
import {
  IconCheck,
  IconExternalLink,
  IconAlertOctagon,
  IconCircleCheck,
  IconInfoCircle,
  IconX,
  IconAlertCircle,
  IconHourglass,
} from "@tabler/icons-react";
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from "../../../utils/notify";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Alert } from "@mantine/core";
import Logo from "../../common/Logo/Logo";
import ProgressiveWizardShell from "./ProgressiveWizardShell";
import DeviceRegistrationModal from "../../payments/DeviceRegistrationModal";
import SubscriptionPricing from "../../SubscriptionPricing";
import { useApp } from "../../../context/AppContext";
import { useSalon } from "../../../context/SalonContext";
import {
  OnboardingProgressResponse,
  UpdateOnboardingProgressRequest,
} from "../types/onboarding";
import { brandColors } from "../../../theme/brandTheme";
import styles from "./ProgressivePaymentsWizard.module.css";

type WizardStep = "intro" | "subscription" | "stripe" | "devices";
type StripeState = "new" | "returning" | "pending" | "success" | "failed";

interface DeviceData {
  label: string;
  registrationCode: string;
}

interface ProgressivePaymentsWizardProps {
  opened: boolean;
  onClose: () => void;
  progress: OnboardingProgressResponse;
  onComplete: (
    postOnboardingProgress: (
      data: UpdateOnboardingProgressRequest,
      salonId?: string,
    ) => Promise<OnboardingProgressResponse>,
  ) => void;
  onSwitchWizard?: (wizard: "employees" | "services" | "payments") => void;
}

export default function ProgressivePaymentsWizard({
  opened,
  onClose,
  progress,
  onComplete,
  onSwitchWizard,
}: ProgressivePaymentsWizardProps) {
  const {
    updateOnboardingProgress,
    createStripeConnectAccount,
    getStripeConnectAccountLink,
    registerStripeConnectTerminalDevice,
    updateSalon,
    updateStaff,
  } = useApp();
  const {
    businessInfo,
    currentUser,
    employees,
    setBusinessInfo,
    setCurrentUser,
    setEmployees,
    refreshBusinessInfo,
  } = useSalon() as {
    businessInfo: any;
    currentUser: any;
    employees: any[];
    setBusinessInfo: React.Dispatch<React.SetStateAction<any>>;
    setCurrentUser: React.Dispatch<React.SetStateAction<any>>;
    setEmployees: React.Dispatch<React.SetStateAction<any[]>>;
    // Multi-location aware refresh of the CURRENT salon record. Never use
    // getSalons()[0] here — after a location switch it returns the OLD salon
    // (legacy salon_staff link) and would clobber businessInfo.
    refreshBusinessInfo: () => Promise<any>;
  };

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasSubscription = businessInfo?.status === "Active";
  const initialStep: WizardStep =
    progress.progressivePayments === null
      ? "intro"
      : hasSubscription
        ? "stripe"
        : "subscription";
  const [currentStep, setCurrentStep] = useState<WizardStep>(initialStep);
  const [isLoading, setIsLoading] = useState(false);
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [deviceData, setDeviceData] = useState<DeviceData>({
    label: "",
    registrationCode: "",
  });
  const [lastStripeError, setLastStripeError] = useState<string | null>(null);

  const getStripeState = (): StripeState => {
    // Check URL parameters first (from Stripe redirects)
    const successParam = searchParams.get("success");
    const canceledParam = searchParams.get("canceled");

    // Failed: Only show failed if we have an API error (not for canceled)
    // Canceled should show "returning" so user can try again
    if (lastStripeError && businessInfo?.stripe_connect_account_id) {
      return "failed";
    }

    // No account ID means new
    if (!businessInfo?.stripe_connect_account_id) {
      return "new";
    }

    // Check if onboarded - handle both boolean true and string "true"
    const isOnboarded =
      businessInfo?.stripe_connect_account_is_onboarded === true ||
      businessInfo?.stripe_connect_account_is_onboarded === "true";

    // Success: account_id exists and is_onboarded === true
    if (isOnboarded) {
      return "success";
    }

    // Pending: User just completed onboarding (success param), but backend hasn't updated yet
    // This means webhook hasn't processed or database hasn't synced
    if (successParam === "true") {
      return "pending";
    }

    // Returning: account_id exists but is_onboarded === false
    // This includes when user canceled (canceledParam) - they can try again
    // User needs to complete the onboarding process
    return "returning";
  };

  const stripeState = getStripeState();

  // Refresh businessInfo when modal opens to check if onboarding status changed
  useEffect(() => {
    if (opened && businessInfo?.id) {
      // Errors are handled (and logged) inside refreshBusinessInfo.
      refreshBusinessInfo();
    }
  }, [opened, businessInfo?.id, refreshBusinessInfo]);

  // Refresh when window regains focus (user returns from Stripe onboarding)
  useEffect(() => {
    const handleFocus = async () => {
      if (businessInfo?.id && businessInfo?.stripe_connect_account_id) {
        // Multi-location aware refresh of the current salon record; errors
        // are handled (and logged) inside refreshBusinessInfo.
        await refreshBusinessInfo();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [businessInfo, refreshBusinessInfo]);

  // Clear URL params after showing alert (handle Stripe redirects)
  // Also clear error state when URL params change
  useEffect(() => {
    const successParam = searchParams.get("success");
    const canceledParam = searchParams.get("canceled");

    // Clear error state if we got a success redirect
    if (successParam === "true") {
      setLastStripeError(null);
    }

    // Clear URL params after a delay to show the message, but keep them
    // long enough for state detection to work
    if (successParam || canceledParam) {
      const timer = setTimeout(() => {
        window.history.replaceState({}, "", window.location.pathname);
      }, 10000); // Longer delay for pending state detection
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const currentSubStep =
    currentStep === "intro"
      ? 1
      : currentStep === "subscription"
        ? 2
        : currentStep === "stripe"
          ? 3
          : 4;

  // Separate sync functions (like PaymentSettings)
  const syncStripeConnectAccountToSalon = useCallback(
    async (stripeAccountId: string) => {
      if (!businessInfo?.id) return;
      // Send ONLY the field this save changes. PUT /business-locations writes every
      // recognized key it receives, so spreading the whole businessInfo
      // record replays potentially-stale values over the DB row.
      await updateSalon({
        id: businessInfo.id,
        stripe_connect_account_id: stripeAccountId,
      });
      setBusinessInfo((prev: any) => ({
        ...prev,
        stripe_connect_account_id: stripeAccountId,
      }));
    },
    [businessInfo?.id, updateSalon, setBusinessInfo],
  );

  const syncStripeConnectAccountToCurrentUser = useCallback(
    async (stripeAccountId: string) => {
      if (!currentUser) return;

      const currentUserWithStripeAccountId = {
        ...currentUser,
        stripe_connect_account_id: stripeAccountId,
      };
      await updateStaff(currentUserWithStripeAccountId);
      setCurrentUser(currentUserWithStripeAccountId);
      // Update employees array
      if (employees) {
        setEmployees(
          employees.map((emp) => ({
            ...emp,
            stripe_connect_account_id:
              emp.id === currentUser.id
                ? stripeAccountId
                : emp.stripe_connect_account_id,
          })),
        );
      }
    },
    [currentUser, employees, updateStaff, setCurrentUser, setEmployees],
  );

  const handleIntroNext = useCallback(async () => {
    // Set payments onboarding to inprogress when moving from intro to subscription
    setIsLoading(true);
    try {
      // Only persist progress when the salon id is known: without the
      // ?salonId= query param the backend silently falls back to a
      // staff-keyed row that can belong to another location.
      if (businessInfo?.id) {
        await updateOnboardingProgress(
          {
            section: "progressive_payments",
            state: "inprogress",
          },
          businessInfo.id,
        );
      }
      setCurrentStep("subscription");
    } catch (error) {
      notifyError("Failed to update progress. Please try again.", {
        title: "Error",
        icon: <IconX size={18} />,
        withCloseButton: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [businessInfo?.id, updateOnboardingProgress]);

  const handleSubscriptionNext = useCallback(() => {
    const hasSubscription = businessInfo?.status === "Active";
    if (!hasSubscription) {
      notifyWarning(
        "Please subscribe first before continuing with payment setup.",
        {
          title: "Subscription Required",
          icon: <IconInfoCircle size={18} />,
          withCloseButton: true,
        },
      );
      return;
    }

    // Payments onboarding is already set to inprogress from intro step
    // Move to the Stripe step only if subscription is active
    setCurrentStep("stripe");
  }, [businessInfo?.status]);

  // Separate function for creating account (like PaymentSettings)
  const handleCreateStripeConnectAccount = useCallback(async (): Promise<
    string | null
  > => {
    try {
      setIsLoading(true);
      setLastStripeError(null); // Clear previous errors
      const accountResponse = await createStripeConnectAccount(
        businessInfo,
        currentUser,
      );
      if (accountResponse?.accountId) {
        await syncStripeConnectAccountToSalon(accountResponse.accountId);
        await syncStripeConnectAccountToCurrentUser(accountResponse.accountId);
        return accountResponse.accountId;
      }
      return null;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create Stripe account";
      setLastStripeError(errorMessage);
      notifyError("Failed to create Stripe account. Please try again.", {
        title: "Error",
        icon: <IconX size={18} />,
        withCloseButton: true,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [
    businessInfo,
    currentUser,
    createStripeConnectAccount,
    syncStripeConnectAccountToSalon,
    syncStripeConnectAccountToCurrentUser,
  ]);

  // Separate function for getting onboarding link (like PaymentSettings)
  const handleGetStripeConnectAccountLink = useCallback(
    async (accountId: string) => {
      try {
        setIsLoading(true);
        setLastStripeError(null); // Clear previous errors
        const linkResponse = await getStripeConnectAccountLink(accountId);

        if (linkResponse?.url) {
          window.open(linkResponse.url, "_blank");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to get Stripe onboarding link";
        setLastStripeError(errorMessage);
        notifyError("Failed to get Stripe onboarding link. Please try again.", {
          title: "Error",
          icon: <IconX size={18} />,
          withCloseButton: true,
        });
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [getStripeConnectAccountLink],
  );

  // Combined handler for connect stripe button
  const handleConnectStripe = useCallback(async () => {
    try {
      let accountId = businessInfo?.stripe_connect_account_id;

      // Create account if none exists
      if (!accountId) {
        accountId = await handleCreateStripeConnectAccount();
      }

      // Get onboarding link if we have an account
      if (accountId) {
        await handleGetStripeConnectAccountLink(accountId);
      }
    } catch (error) {
      // Error notifications already handled in individual functions
    }
  }, [
    businessInfo?.stripe_connect_account_id,
    handleCreateStripeConnectAccount,
    handleGetStripeConnectAccountLink,
  ]);

  const handleRegisterDevice = useCallback(async () => {
    if (!deviceData.label || !deviceData.registrationCode) {
      notifyWarning("Please enter both a device label and registration code.", {
        title: "Missing Information",
        icon: <IconInfoCircle size={18} />,
        withCloseButton: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await registerStripeConnectTerminalDevice(
        deviceData,
        businessInfo,
      );
      if (response?.id) {
        // Update local state with the device ID from the API response
        const updatedBusinessInfo = {
          ...businessInfo,
          stripe_terminal_device_id: response.id,
        };
        setBusinessInfo(updatedBusinessInfo);
        // Persist to database so it survives page refresh
        await updateSalon(updatedBusinessInfo);
        notifySuccess("Device registered successfully!", {
          title: "Success",
          icon: <IconCheck size={18} />,
          withCloseButton: true,
        });
        setDeviceModalOpen(false);
        setDeviceData({ label: "", registrationCode: "" });
      }
    } catch (error) {
      notifyError("Failed to register device. Please try again.", {
        title: "Error",
        icon: <IconX size={18} />,
        withCloseButton: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    deviceData,
    businessInfo,
    registerStripeConnectTerminalDevice,
    setBusinessInfo,
    updateSalon,
  ]);

  // Validate Stripe setup is complete before allowing next step
  const handleStripeNext = useCallback(() => {
    // Check if Stripe account exists and is onboarded
    const hasStripeAccount = !!businessInfo?.stripe_connect_account_id;
    const isOnboarded =
      businessInfo?.stripe_connect_account_is_onboarded === true ||
      businessInfo?.stripe_connect_account_is_onboarded === "true";

    if (!hasStripeAccount) {
      notifyWarning(
        "Please create and connect your Stripe account before continuing.",
        {
          title: "Stripe Account Required",
          icon: <IconInfoCircle size={18} />,
          withCloseButton: true,
        },
      );
      return;
    }

    if (!isOnboarded) {
      notifyWarning(
        "Please complete your Stripe onboarding before continuing. Click 'Connect with Stripe' to finish setting up your account.",
        {
          title: "Stripe Onboarding Required",
          icon: <IconInfoCircle size={18} />,
          withCloseButton: true,
        },
      );
      return;
    }

    // Stripe is set up, allow progression to devices step
    setCurrentStep("devices");
  }, [businessInfo]);

  // Check if Stripe setup is complete (for disabling Next button)
  const isStripeSetupComplete = useCallback(() => {
    const hasStripeAccount = !!businessInfo?.stripe_connect_account_id;
    const isOnboarded =
      businessInfo?.stripe_connect_account_is_onboarded === true ||
      businessInfo?.stripe_connect_account_is_onboarded === "true";
    return hasStripeAccount && isOnboarded;
  }, [businessInfo]);

  const handleComplete = useCallback(() => {
    // Fire POST async (don't wait), update local state optimistically, route based on state
    // onComplete handles: optimistic state update, routing decision, and POST completion
    onComplete(updateOnboardingProgress);
  }, [updateOnboardingProgress, onComplete]);

  const renderIntroStep = () => {
    return (
      <Stack
        align="center"
        justify="center"
        gap="md"
        className={styles.introContainer}
      >
        <Logo variant="icon" size="lg" />
        <Text size="xl" fw={700} ta="center">
          Add Payments
        </Text>
        <Stack gap="xs" ta="center" className={styles.benefitsList}>
          <Group gap="xs" justify="center">
            <IconCheck size={16} color="#868e96" />
            <Text size="sm" ta="center">
              Connect Stripe to securely process service payments.
            </Text>
          </Group>
          <Group gap="xs" justify="center">
            <IconCheck size={16} color="#868e96" />
            <Text size="sm" ta="center">
              Use in-store POS devices for fast, reliable payments.
            </Text>
          </Group>
          <Group gap="xs" justify="center">
            <IconCheck size={16} color="#868e96" />
            <Text size="sm" ta="center">
              Simplify reporting and stay on top of revenue.
            </Text>
          </Group>
        </Stack>
      </Stack>
    );
  };

  const renderSubscriptionStep = () => {
    const hasSubscription = businessInfo?.status === "Active";

    if (hasSubscription) {
      return (
        <Stack className={styles.stripeContainer}>
          <Text size="lg" fw={700}>
            You are eligible to start accepting payments!
          </Text>
          <Text size="sm" c="dimmed" mt="sm">
            Your subscription is active. Click Next to continue setting up
            payments.
          </Text>
        </Stack>
      );
    }

    return (
      <Stack className={styles.subscriptionContainer}>
        <SubscriptionPricing />
      </Stack>
    );
  };

  // Render success/cancel alerts based on URL parameters
  const renderStatusAlert = () => {
    if (searchParams.get("success") === "true") {
      return (
        <Alert
          icon={<IconCheck size="1.1rem" />}
          title="Success!"
          color="green"
          mb="md"
        >
          Successfully onboarded Stripe Connect. Your account is now ready to
          accept payments.
        </Alert>
      );
    }

    if (searchParams.get("canceled") === "true") {
      return (
        <Alert
          icon={<IconAlertCircle size="1.1rem" />}
          title="Warning"
          color="yellow"
          mb="md"
        >
          Warning: Stripe Account Onboarding is incomplete. Access to Payments
          will be restricted until onboarding is completed.
        </Alert>
      );
    }

    return null;
  };

  const renderStripeStep = () => {
    switch (stripeState) {
      case "new":
        return (
          <Stack className={styles.stripeContainer}>
            {renderStatusAlert()}
            <Text size="lg" fw={700}>
              Accept Payments
            </Text>
            <Text size="sm" c="dimmed">
              To accept payments, connect your account with{" "}
              <Text span fw={600}>
                Stripe
              </Text>
              , our secure provider for processing, payouts, and compliance.
            </Text>
            <Button
              size="md"
              mt="xl"
              fullWidth
              rightSection={<IconExternalLink size={16} />}
              onClick={handleConnectStripe}
              loading={isLoading}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Connect with Stripe
            </Button>
          </Stack>
        );

      case "returning":
        return (
          <Stack className={styles.stripeContainer}>
            {renderStatusAlert()}
            <Text size="lg" fw={700}>
              Finish Connecting Your Stripe account
            </Text>
            <Text size="sm" c="dimmed">
              You've already started setting up Stripe: pick up where you left
              off and start accepting secure payments online or in-store.
            </Text>
            <Button
              size="md"
              mt="xl"
              fullWidth
              rightSection={<IconExternalLink size={16} />}
              onClick={handleConnectStripe}
              loading={isLoading}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Connect with Stripe
            </Button>
          </Stack>
        );

      case "pending":
        return (
          <Stack className={styles.stripeContainer}>
            <Box className={styles.statusIcon}>
              <IconHourglass size={48} color={brandColors.teal} />
            </Box>
            <Text size="lg" fw={700} ta="center" mt="md">
              Your Stripe account is being verified
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              We're processing your Stripe onboarding. Verification usually
              takes a few moments. The page will update automatically when
              complete.
            </Text>
            <Text
              size="sm"
              c="teal"
              ta="center"
              mt="xs"
              td="underline"
              style={{ cursor: "pointer" }}
              onClick={async () => {
                // Refresh businessInfo to check if status updated
                // (multi-location aware; errors handled inside).
                await refreshBusinessInfo();
              }}
            >
              Click to refresh status
            </Text>
          </Stack>
        );

      case "success":
        return (
          <Stack className={styles.stripeContainer}>
            <Box className={styles.statusIcon}>
              <IconCircleCheck size={48} color={brandColors.teal} />
            </Box>
            <Text size="lg" fw={700} ta="center" mt="md">
              Stripe account connected
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              You're ready to start accepting payments right away.
            </Text>
            <Text size="sm" ta="center" mt="xs">
              Visit{" "}
              <Text
                span
                c="teal"
                td="underline"
                component="a"
                href="/admin/payments/settings"
              >
                Payments
              </Text>{" "}
              to make changes.
            </Text>
          </Stack>
        );

      case "failed":
        return (
          <Stack className={styles.stripeContainer}>
            <Box className={styles.statusIcon}>
              <IconAlertOctagon size={48} color={brandColors.burntOrange} />
            </Box>
            <Text size="lg" fw={700} ta="center" mt="md">
              Stripe onboarding incomplete
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              {lastStripeError
                ? `Error: ${lastStripeError}`
                : "The Stripe onboarding process was not completed. Access to Payments will be restricted until onboarding is finished."}
            </Text>
            <Button
              size="md"
              mt="xl"
              fullWidth
              rightSection={<IconExternalLink size={16} />}
              onClick={handleConnectStripe}
              loading={isLoading}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Retry Stripe Connection
            </Button>
          </Stack>
        );

      default:
        return null;
    }
  };

  const renderDevicesStep = () => (
    <Stack className={styles.devicesContainer}>
      <Text size="lg" fw={700}>
        Add Devices
      </Text>
      <Text size="sm" c="dimmed">
        Add a payment device to start accepting in-person payments. Connect your
        POS hardware and link it to your account.
      </Text>

      <Button
        size="md"
        mt="xl"
        fullWidth
        rightSection={<IconExternalLink size={16} />}
        onClick={() =>
          window.open("https://stripe.com/terminal/readers", "_blank")
        }
        styles={{
          root: {
            backgroundColor: brandColors.teal,
            "&:hover": {
              backgroundColor: brandColors.teal,
            },
          },
        }}
      >
        I'm interested in buying a device
      </Button>

      <Button
        variant="subtle"
        color="teal"
        size="md"
        mt="md"
        fullWidth
        onClick={() => setDeviceModalOpen(true)}
      >
        + Connect an existing device
      </Button>

      {businessInfo?.stripe_terminal_device_id && (
        <Group gap="xs" align="center" className={styles.deviceConnected}>
          <IconCircleCheck size={20} color="#a2d55e" />
          <Text size="sm" c="green">
            Device connected
          </Text>
        </Group>
      )}
    </Stack>
  );

  const renderContent = () => {
    switch (currentStep) {
      case "intro":
        return renderIntroStep();
      case "subscription":
        return renderSubscriptionStep();
      case "stripe":
        return renderStripeStep();
      case "devices":
        return renderDevicesStep();
      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (currentStep) {
      case "intro":
        return (
          <>
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button
              onClick={handleIntroNext}
              loading={isLoading}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Next
            </Button>
          </>
        );
      case "subscription":
        const hasSubscription = businessInfo?.status === "Active";
        return (
          <>
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => setCurrentStep("intro")}>
              Back
            </Button>
            <Button
              onClick={handleSubscriptionNext}
              loading={isLoading}
              disabled={!hasSubscription}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Next
            </Button>
          </>
        );
      case "stripe":
        const stripeComplete = isStripeSetupComplete();
        return (
          <>
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => setCurrentStep("subscription")}
            >
              Back
            </Button>
            <Button
              onClick={handleStripeNext}
              disabled={!stripeComplete}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Next
            </Button>
          </>
        );
      case "devices":
        return (
          <>
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => setCurrentStep("stripe")}>
              Back
            </Button>
            <Button
              onClick={handleComplete}
              loading={isLoading}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Submit
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ProgressiveWizardShell
        opened={opened}
        onClose={onClose}
        currentStep="payments"
        currentSubStep={currentSubStep}
        totalSubSteps={4}
        employeesStatus={progress.progressiveEmployees}
        servicesStatus={progress.progressiveServices}
        paymentsStatus={progress.progressivePayments}
        footer={<Group>{renderFooter()}</Group>}
        onStepClick={onSwitchWizard}
      >
        {renderContent()}
      </ProgressiveWizardShell>

      {/* Device Registration Modal */}
      <DeviceRegistrationModal
        opened={deviceModalOpen}
        onClose={() => {
          setDeviceModalOpen(false);
          setDeviceData({ label: "", registrationCode: "" });
        }}
        onSubmit={handleRegisterDevice}
        deviceData={deviceData}
        setDeviceData={setDeviceData}
        isLoading={isLoading}
      />
    </>
  );
}
